/* ui.js — site-wide islands: predictive search overlay + "project list" (trade wishlist).
 * Deferred, tiny, hydrates on interaction. */
(() => {
  'use strict';
  const $ = (s, r = document) => r.querySelector(s);

  /* ---------- how did the shopper just interact? ----------
   * Luke, 2026-09-20, iPhone Safari: "When untoggling the mobile nav menu
   * closed it still appears in a selected state."
   *
   * The CSS half of that (a stuck :hover fill) is fixed in theme.css. This is
   * the other half: every surface in this file that closes returns focus to
   * the control that opened it, which is exactly right for a keyboard user
   * and wrong for a thumb — iOS Safari focuses a <button> on tap, so the
   * toggle ends the gesture focused, and WebKit's :focus-visible heuristics
   * are loose enough to paint a ring on it (critical.css gives every focusable
   * a 2px accent outline).
   *
   * So record the modality and let each close() decide: restore focus after a
   * key, drop it after a pointer. `data-input` on <html> rather than a module
   * export because the cart island is a separate bundle that needs the same
   * answer (src/islands/cart/Bin.jsx). ABSENT MEANS KEYBOARD: if this listener
   * has not run yet, the accessible behaviour is the one we fall back to.
   *
   * Only keys that can ACTIVATE or MOVE focus count. A shopper typing in the
   * search field must not turn the next close into a focus restore, so plain
   * character keys are ignored. */
  const KEYS = new Set(['Tab', 'Escape', 'Enter', ' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End']);
  const setInput = (v) => { document.documentElement.dataset.input = v; };
  document.addEventListener('keydown', (e) => { if (KEYS.has(e.key)) setInput('key'); }, true);
  document.addEventListener('pointerdown', () => setInput('pointer'), true);
  // Safari < 13 and any browser without Pointer Events still reports touch
  document.addEventListener('touchstart', () => setInput('pointer'), { capture: true, passive: true });
  const byKeyboard = () => document.documentElement.dataset.input !== 'pointer';
  /* Hand focus back to `el` if the shopper is on a keyboard, otherwise make
   * sure it is not left wearing one. blur() is conditional: blurring an
   * element that does not have focus is a no-op, but blurring one the shopper
   * has deliberately moved to (they tapped something else while the surface
   * was closing) would steal it. */
  const releaseFocus = (el) => {
    if (!el || !document.contains(el)) return;
    if (byKeyboard()) { el.focus(); return; }
    if (document.activeElement === el) el.blur();
  };
  // quick-view HTML warmed on tile hover; section responses are no-store so
  // the HTTP cache can never serve them — hold the in-flight promise instead
  const qvCache = new Map();
  const qvWarm = (handle) => {
    if (!handle || qvCache.has(handle)) return;
    if (qvCache.size > 30) qvCache.clear();
    qvCache.set(handle, fetch('/products/' + handle + '?section_id=quick-view')
      .then((r) => (r.ok ? r.text() : null)).catch(() => null));
  };

  /* ---------- predictive search ---------- */
  const overlay = $('#SearchOverlay');
  if (overlay) {
    const input = $('[data-search-input]', overlay);
    const results = $('[data-search-results]', overlay);
    const open = () => { overlay.setAttribute('aria-hidden', 'false'); document.body.classList.add('drawer-open'); setTimeout(() => input.focus(), 50); };
    const close = () => { overlay.setAttribute('aria-hidden', 'true'); document.body.classList.remove('drawer-open'); };
    let t, ctrl;
    document.addEventListener('click', (e) => {
      // universal search bar replaced the overlay trigger (feat/universal-search-bar)
      if (e.target.closest('[data-search-close]')) close();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    // native predictive search — skipped when Typesense instant search is enabled (it takes over)
    input?.addEventListener('input', () => {
      if (window.TYPESENSE) return;
      clearTimeout(t);
      const q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      t = setTimeout(async () => {
        ctrl?.abort(); ctrl = new AbortController();
        const url = `${window.routes.search_url}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product`;
        try {
          const res = await fetch(url, { signal: ctrl.signal });
          results.innerHTML = await res.text();
        } catch {}
      }, 160);
    });
  }

  /* project list + wishlist now live in project-list.js (trade projects) */
  /* ---------- shared variant switcher (PDP + quick view) ---------- */
  const fmtMoney = (c) => { try { return (c / 100).toLocaleString('en-AU', { style: 'currency', currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD' }); } catch { return '$' + (c / 100).toFixed(2); } };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-option-value]');
    if (!btn) return;
    const form = btn.closest('[data-product-form]');
    const grp = btn.closest('[role="radiogroup"]');
    if (!form || !grp) return;
    grp.querySelectorAll('[data-option-value]').forEach((b) => { const on = b === btn; b.classList.toggle('is-selected', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); });
    const legend = btn.closest('.product__option')?.querySelector('[data-selected-value]'); if (legend) legend.textContent = btn.dataset.value;
    // variant data: prefer section-scoped, fall back to the form, then document-wide
    const jsonEl = (btn.closest('[data-section-id]') || form).querySelector('[data-product-json]')
      || document.querySelector('[data-product-json]');
    const variants = JSON.parse(jsonEl?.textContent || '[]');
    const rows = [...form.querySelectorAll('.product__option')];
    const opts = rows.map((f) => f.querySelector('[aria-checked="true"]')?.dataset.value || null);
    let match = variants.find((v) => opts.every((o, i) => !o || v.options[i] === o));
    /* THE DEAD END (found on production 2026-09-21 by
     * scripts/variant-gallery-gate.mjs, on the Aulic parent in Luke's report).
     *
     * This used to be `if (!match) return;` — and on a product whose option
     * grid is SPARSE, the combination the shopper just asked for often does
     * not exist. On aulic-varcelli-tier-wall-hung-750mm the stone top "Snow"
     * is offered in 750/900/1200/1500 but NOT in 1800, whose stone tops are
     * Palis, Alpine, Cato and Pure. So with Snow held, pressing "1800mm" fell
     * straight out of this handler: the chip had ALREADY been repainted as
     * selected four lines above, the variant, the price, the SKU and the
     * gallery all stayed on the 1500mm Snow, and nothing told the shopper.
     * Luke, 2026-09-21: "I'd expect the base variant to be changing with the
     * size, colour, etc." — a press that visibly selects a size and changes
     * nothing else is that report, in its second form.
     *
     * Sparse grids are not an edge case here: they are what the variant
     * consolidation programme PRODUCES. Every family it merges stacks sizes
     * that the supplier does not offer in every finish.
     *
     * So: keep the option the shopper actually pressed, and relax the others.
     * Among the variants carrying that value, prefer an AVAILABLE one, then
     * the one that keeps the most of the shopper's other choices. Rejected:
     * disabling the chips that lead nowhere, which is the other common
     * pattern — with three options and a sparse grid it greys out most of the
     * grid depending on click order, so the same product looks differently
     * stocked depending on the path taken to it, and a shopper who wants
     * 1800mm can no longer even ask for it.
     *
     * Every row is then repainted from the variant we actually landed on
     * (below), so no chip is left claiming a value the selection does not
     * have — which was the real damage: the UI lied, silently. */
    if (!match) {
      const gi = rows.indexOf(btn.closest('.product__option'));
      const want = btn.dataset.value;
      if (gi > -1) {
        match = variants
          .filter((v) => v.options[gi] === want)
          .map((v) => ({ v, keep: opts.reduce((n, o, i) => n + (i !== gi && o && v.options[i] === o ? 1 : 0), 0) }))
          .sort((a, b) => (Number(b.v.available) - Number(a.v.available)) || (b.keep - a.keep))[0]?.v;
      }
    }
    if (!match) return;
    /* Repaint EVERY option row from the variant that was actually selected.
     * Idempotent on the row that was clicked, and the only thing that keeps
     * the other rows honest after a relaxed match. */
    rows.forEach((row, i) => {
      const value = match.options[i];
      row.querySelectorAll('[data-option-value]').forEach((b) => {
        const on = b.dataset.value === value;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      const lg = row.querySelector('[data-selected-value]');
      if (lg && value) lg.textContent = value;
    });
    // price/sku/legend live OUTSIDE the <form>; scope display updates to the
    // whole section (fall back to the product container, then the form).
    const root = btn.closest('[data-section-id]') || btn.closest('.product') || form;
    const q = (s) => root.querySelector(s);
    if (form.querySelector('[data-variant-id]')) form.querySelector('[data-variant-id]').value = match.id;
    if (q('[data-current]')) q('[data-current]').textContent = fmtMoney(match.price);
    if (q('[data-add-price]')) q('[data-add-price]').textContent = fmtMoney(match.price);
    // a REAL saving only — the $1.00 floor (window.STBadges.isSaving, cents
    // here). Under it the compare-at is price-rule rounding: the Liquid first
    // paint already hides it, and a variant change must not bring it back.
    const B = window.STBadges;
    const discounted = !!(B && B.isSaving && B.isSaving(match.price, match.compare_at_price, 'cents'));
    const cmp = q('[data-compare]');
    if (cmp) { cmp.hidden = !discounted; cmp.textContent = discounted ? fmtMoney(match.compare_at_price) : ''; }
    const sv = q('[data-save]');
    if (sv) { sv.hidden = !discounted; sv.textContent = discounted ? (sv.dataset.savePrefix || 'Save ') + fmtMoney(match.compare_at_price - match.price) : ''; }
    if (q('[data-price]')) q('[data-price]').classList.toggle('is-discounted', discounted);
    if (q('[data-sku]')) q('[data-sku]').textContent = match.sku || '';
    const add = q('[data-add]'), txt = q('[data-add-text]');
    if (add) { add.disabled = !match.available; if (txt) txt.textContent = match.available ? 'Add to cart' : 'Sold out'; }
    // the imagery follows the variant. The PDP gallery listens for
    // variant:change itself (product.js carousel) — here we only handle
    // single-frame contexts (quick view) by swapping the image in place.
    const fm = match.featured_media;
    const fiSrc = (match.featured_image && match.featured_image.src)
      || (fm && fm.preview_image && fm.preview_image.src);
    if (fiSrc) {
      const solo = q('.qv__media img');
      if (solo) {
        solo.removeAttribute('srcset');
        // ONE layer only. responsive-image's placeholder paints the image the
        // section rendered WITH into the element's background; swapping src
        // leaves that stale copy behind the new one, and a cut-out PNG render
        // (transparent around the product) shows it straight through — the
        // previous finish ghosting under the selected one. Kill it here rather
        // than trusting the new image's own onload, which lands a frame late.
        solo.style.background = '';
        solo.src = fiSrc + (fiSrc.indexOf('?') > -1 ? '&' : '?') + 'width=800';
      }
    }
    form.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: match }));
  });

  /* ---------- the buy row (snippets/buy-row.liquid), PDP + quick view ------
   * -/+ buttons, arrow keys, typed input and its normalisation (empty / 0 /
   * non-numeric -> 1 on change), the inventory cap, the Add-to-cart price
   * label (qty x unit, in the same en-AU currency format the variant
   * switcher above paints) and the reset to 1 after a confirmed add.
   *
   * Cap: only when the selected variant tracks stock (inventory_management)
   * with policy deny — the cart's own 422 rule, applied before the request so
   * the note reads "Only N available" here instead of after a rollback.
   *
   * THIS CODE IS UNCHANGED FROM assets/product.js, where it lived until
   * 2026-09-21; only its scope is. It was a boot-time block inside a file
   * that returns early unless `.product[data-section-id]` is on the page, so
   * the quick view — the same buy box, rendered through the Section Rendering
   * API into a modal, after load — could never have had it. Luke's two
   * screenshots of 2026-09-21 are what that cost: the modal offered a bare
   * "Add to cart" while the product page offered "- 1 +" and "Add to cart
   * $361.00".
   *
   * `root` is the container to read display elements out of: the section on
   * the PDP, the modal body in the quick view. Everything it queries is
   * scoped to that root, which is what lets a quick view opened OVER a PDP
   * drive its own stepper and its own price pill without either row reaching
   * into the other's. Binding twice on one form would double every listener,
   * so a bound form is marked and skipped. */
  function initBuyRow(root) {
    if (!root) return;
    const form = root.querySelector('[data-product-form] form');
    const qtyEl = form && form.querySelector('[data-qty-input]');
    if (!form || !qtyEl || form.dataset.buyRowBound) return;
    form.dataset.buyRowBound = '1';
    const down = form.querySelector('[data-qty-down]');
    const up = form.querySelector('[data-qty-up]');
    const label = root.querySelector('[data-add-price]');
    const msg = root.querySelector('[data-qty-msg]');
    let variants = [];
    try { variants = JSON.parse(root.querySelector('[data-product-json]')?.textContent || '[]') || []; } catch {}
    const selected = () => {
      const id = parseInt(form.querySelector('[data-variant-id]')?.value || '0', 10);
      return variants.find((v) => v.id === id) || null;
    };
    let unit = (() => { const v = selected(); return v ? v.price : parseInt(label?.dataset.unitPrice || '0', 10); })();
    // available quantity when the variant denies overselling; null = uncapped
    const capOf = (v) => (v && v.inventory_management && v.inventory_policy === 'deny' && Number.isFinite(v.inventory_quantity) ? Math.max(1, v.inventory_quantity) : null);
    let cap = capOf(selected());
    let msgTimer = null;
    const say = (text) => {
      if (!msg) return;
      clearTimeout(msgTimer);
      msg.textContent = text; msg.hidden = !text;
      if (text) msgTimer = setTimeout(() => { if (msg.textContent === text) { msg.textContent = ''; msg.hidden = true; } }, 6000);
    };
    const read = () => { const n = parseInt(String(qtyEl.value).replace(/[^\d]/g, ''), 10); return Number.isFinite(n) && n >= 1 ? n : 1; };
    const paint = (n) => {
      if (String(n) !== qtyEl.value) qtyEl.value = String(n);
      if (down) down.disabled = n <= 1;
      if (up) up.disabled = cap !== null && n >= cap;
      if (label && unit) label.textContent = fmtMoney(unit * n);
    };
    // clamp to the cap, tell the shopper when that changed what they asked for
    const set = (n, notify) => {
      let q = Math.max(1, Math.floor(n) || 1);
      if (cap !== null && q > cap) { q = cap; if (notify !== false) say('Only ' + cap + ' available'); }
      const changed = String(q) !== qtyEl.value;
      paint(q);
      if (changed) qtyEl.dispatchEvent(new Event('change', { bubbles: true }));
      return q;
    };
    down?.addEventListener('click', () => set(read() - 1));
    up?.addEventListener('click', () => { const n = read(); if (cap !== null && n >= cap) say('Only ' + cap + ' available'); else set(n + 1); });
    qtyEl.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowUp') { e.preventDefault(); up?.click(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); set(read() - 1); }
    });
    // live label while typing (a valid number only — an emptied field waits for change)
    qtyEl.addEventListener('input', () => {
      if (!/^\d+$/.test(qtyEl.value) || parseInt(qtyEl.value, 10) < 1) return;
      const n = cap !== null ? Math.min(cap, parseInt(qtyEl.value, 10)) : parseInt(qtyEl.value, 10);
      if (down) down.disabled = n <= 1;
      if (label && unit) label.textContent = fmtMoney(unit * n); // the field itself is left alone until change
    });
    // capture phase: the field is normalised BEFORE purchase-block.js (a
    // target-phase listener on the same element) reads it for the quote
    qtyEl.addEventListener('change', () => set(read()), true);
    /* finish swatch / option switch: keep the quantity, re-price the label.
     * Bound on the row's own [data-product-form] HOST, not on the section and
     * not on the inner <form>. The switcher dispatches `variant:change` on
     * `btn.closest('[data-product-form]')` — the <product-form> element that
     * WRAPS the form — and a parent's event never reaches a child, so a
     * listener on the inner form heard nothing and `unit` went stale: the
     * quick-view gate read "$722.00" at qty 2 after switching to a $470
     * variant (2 x the price the dialog opened with). The PDP had the same
     * hole once the block moved off the section. The host is still below
     * anything shared, so a quick view opened over a PDP (two rows in one
     * document) cannot hear the other row's changes. */
    const host = form.closest('[data-product-form]') || form;
    host.addEventListener('variant:change', (e) => {
      const v = e.detail; if (!v) return;
      unit = v.price; cap = capOf(v);
      set(read());
    });
    // a confirmed add (theme.js -> cart store resolved true): back to 1 —
    // the drawer line carries the quantity that just went in
    form.addEventListener('cart:added', () => { set(1, false); say(''); });
    paint(read());
  }
  // the PDP's row, at boot. The quick view's is initialised per render below.
  initBuyRow(document.querySelector('.product[data-section-id]'));

  /* ---------- quick view ---------- */
  const qv = $('#QuickView');
  if (qv) {
    const qbody = $('[data-qv-body]', qv);
    /* The tile that opened the dialog. Focus goes back to it on close — the
     * shopper's place in the grid, not the top of the document. Kept as an
     * element reference and checked with document.contains before use: an
     * infinite-scroll page or a Typesense re-render can replace the tile
     * while the dialog is open, and focusing a detached node silently sends
     * focus to <body>. */
    let opener = null;
    /* Focus must not be left inside a subtree that has just become
     * aria-hidden, and it must not be thrown to the top of the document
     * either. On a keyboard it goes back to the TILE that opened the dialog —
     * the shopper's place in the grid — and on a pointer it is simply lifted
     * out of the hidden dialog, because a mouse user has no focus ring to put
     * anywhere. releaseFocus alone is not enough on the add path: the active
     * element there is the Add button, not the opener, so its "blur only what
     * had focus" rule would leave focus on a hidden button. */
    const returnFocus = () => {
      const active = document.activeElement;
      const stranded = active && active !== document.body && qv.contains(active);
      if (opener && document.contains(opener) && byKeyboard()) { opener.focus(); return; }
      if (stranded && active.blur) active.blur();
      else releaseFocus(opener);
    };
    const closeQv = (opts = {}) => {
      if (qv.getAttribute('aria-hidden') === 'true') return;
      qv.setAttribute('aria-hidden', 'true');
      /* The cart drawer uses the same body class. Dropping it here while the
       * drawer is opening in the SAME task (theme.js's submit handler runs
       * before this file's, both synchronous) would unlock the page behind an
       * open drawer, so on the add path the class is left for the drawer to
       * own and release. */
      if (!opts.keepLock) document.body.classList.remove('drawer-open');
      returnFocus();
    };
    const showQv = () => {
      qv.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
    };
    document.addEventListener('click', async (e) => {
      const t = e.target.closest('[data-quick-view]');
      if (t) {
        e.preventDefault();
        opener = t;
        qbody.innerHTML = '<div class="qv-modal__loading" role="status" aria-label="Loading"><span class="loadbar"></span></div>';
        showQv();
        try {
          const warmed = qvCache.get(t.dataset.handle);
          let html = warmed ? await warmed : null;
          if (!html) {
            // a stale index entry 404s here but Shopify still renders the
            // section body — only trust it when the handle actually resolved
            const r = await fetch(`/products/${t.dataset.handle}?section_id=quick-view`);
            if (r.ok) html = await r.text();
          }
          qbody.innerHTML = html || '<div class="qv"><div class="qv__info qv__info--missing"><h2 class="qv__title">This product is no longer available</h2><p class="product__vendor">It may have been renamed or replaced.</p><a class="btn btn--add" href="' + ((window.routes && window.routes.search_url) || '/search') + '">Search the store</a></div></div>';
          // the row only exists now: the section body has just been parsed
          initBuyRow(qbody);
        }
        catch { location.href = '/products/' + t.dataset.handle; }
      }
      if (e.target.closest('[data-qv-close]')) closeQv();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeQv(); });

    /* ---- add to cart from the quick view hands off to the cart drawer ----
     * Luke, 2026-09-21: "When clicking add to cart from the quick view it
     * should close the quick view popup and show the cart drawer popout.
     * Cart drawer popout is working as expected, add to cart from quick view
     * stays there while I would be expecting it to disappear."
     *
     * Measured on production (release 372ed9f) the same morning, sampling
     * both dialogs' aria-hidden every 50 ms across an add: the drawer went
     * open at +139 ms and the quick view NEVER closed. Two stacked overlays,
     * the drawer behind the one the shopper is looking at.
     *
     * THE DRAWER OPENS OPTIMISTICALLY, so this closes optimistically too.
     * theme.js's submit handler (bound earlier — theme.js is loaded before
     * ui.js in layout/theme.liquid) calls cart.add(), which paints the line
     * and opens the drawer synchronously, in this same task. Closing here, in
     * the next listener of the same event, means the browser composites ONE
     * frame: dialog gone, drawer open, line already in its band. Waiting for
     * the add to RESOLVE instead would leave the dialog sitting over an open
     * drawer for the length of a round trip — which is the bug, just shorter.
     *
     * `keepLock` because both surfaces share `body.drawer-open` and the
     * drawer now owns it.
     *
     * Guarded on there being something to add: a form with no variant id, or
     * a disabled (sold out) button, submits nothing, and the dialog must not
     * vanish on a press that did nothing. A cart island that never loaded is
     * also left alone — theme.js lets that form post natively to /cart. */
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('product-form form, form[action$="/cart/add"]');
      if (!form || !qv.contains(form)) return;
      if (!(window.SydneyTaps && window.SydneyTaps.cart)) return;
      const id = parseInt(form.querySelector('[data-variant-id]')?.value || '0', 10);
      const btn = form.querySelector('[data-add]');
      if (!id || (btn && btn.disabled)) return;
      const err = qv.querySelector('[data-add-error]');
      if (err) { err.hidden = true; err.textContent = ''; }
      closeQv({ keepLock: true });
    });

    /* A rejected add brings the dialog back with the reason on it.
     * On the PDP a 422 is reported by the drawer, which is still on screen;
     * from the quick view the shopper has been handed to the drawer and the
     * form that failed is no longer anywhere they can see, so the dialog —
     * with their variant and their quantity still selected — is where the
     * message belongs and where the retry happens. */
    document.addEventListener('cart:add-failed', (e) => {
      const form = e.target && e.target.closest ? e.target.closest('form') : null;
      if (!form || !qv.contains(form)) return;
      const err = qv.querySelector('[data-add-error]');
      if (err) {
        /* The cart island's own wording for what just happened — Shopify's
         * 422 text for a stock limit, its offline sentence for a dropped
         * request (src/islands/cart/store.js friendly()). Reading it rather
         * than inventing a second vocabulary means the dialog and the drawer
         * say the same thing about the same failure. */
        let why = '';
        try { why = (window.SydneyTaps.cart.get().messages || {}).cart || ''; } catch {}
        err.textContent = why || 'Sorry, we could not add that to your cart. Please try again.';
        err.hidden = false;
      }
      showQv();
      if (err && err.scrollIntoView) err.scrollIntoView({ block: 'nearest' });
    });
  }

  /* ---------- card swatch hover -> variant image preview ----------
   * Hovering a finish chip swaps the card's main photo to that variant's
   * image (data-swatch-preview, emitted by snippets/swatch.liquid); leaving
   * the strip restores the original. The card's own alt-image crossfade is
   * suppressed while a preview is active (CSS .is-variant-preview). */
  document.addEventListener('pointerover', (e) => {
    const chip = e.target.closest && e.target.closest('.card__swatches [data-swatch-preview]');
    if (!chip) return;
    const card = chip.closest('.card');
    const img = card && card.querySelector('.card__media img:not(.card__media-alt)');
    if (!img) return;
    if (!('origSrc' in img.dataset)) {
      img.dataset.origSrc = img.getAttribute('src') || '';
      img.dataset.origSrcset = img.getAttribute('srcset') || '';
    }
    img.removeAttribute('srcset');
    img.src = chip.dataset.swatchPreview;
    card.classList.add('is-variant-preview');
  });
  document.addEventListener('pointerout', (e) => {
    const strip = e.target.closest && e.target.closest('.card__swatches');
    if (!strip || (e.relatedTarget && strip.contains(e.relatedTarget))) return;
    const card = strip.closest('.card');
    const img = card && card.querySelector('.card__media img:not(.card__media-alt)');
    if (!img || !('origSrc' in img.dataset)) return;
    img.src = img.dataset.origSrc;
    if (img.dataset.origSrcset) img.setAttribute('srcset', img.dataset.origSrcset);
    card.classList.remove('is-variant-preview');
  });

  /* ---------- filter-driven finish: the grid shows what you filtered for ----
   * Filtering to a single finish (f=colours~Black) left every tile on its
   * default photo, so a "Black" chip sat above a grid of chrome.
   *
   * Verified on preview before this version: a naive exact-label match NEVER
   * fires, for two reasons that were not visible from the code alone.
   *   1. The facet value is a COLOUR BUCKET ("Black"); chips carry finish
   *      NAMES ("Matte Black", "Graphite"). The bucket is derived server-side
   *      in backend/facets/derive-colours.mjs; the same derivation is ported
   *      below and MUST be kept in step with that file's RULES table.
   *   2. The preview attribute differs by renderer: SSR tiles carry
   *      data-swatch-preview (snippets/swatch.liquid); island tiles carry
   *      data-fin-img (typesense-plp.js hitTemplate). Island tiles whose hit
   *      has no finish_images render decorative dots with NO image — those
   *      cards keep their default photo, there is nothing to swap to.
   *
   * Only when EXACTLY ONE finish is refined: with two or more there is no
   * single right answer and silently picking one would misrepresent the range.
   *
   * Both hover handlers (ui.js SSR, typesense-plp.js island) restore to
   * img.dataset.origSrc on leave — so while a filter is active origSrc IS the
   * filtered photo, and hover-out cannot silently drop the shopper back to
   * chrome. data-baseSrc keeps the true default for when the filter clears,
   * including srcset (PR #151 tuned those bands; dropping srcset permanently
   * would quietly undo that work).
   */
  // KEEP IN STEP with RULES in backend/facets/derive-colours.mjs — same order,
  // same patterns. First match wins; >2 distinct matches = Mixed.
  const COLOUR_RULES = [
    ['Brushed Nickel', /brushed\s*nickel|nickel/],
    ['Gunmetal', /gun\s*metal|gunmetal/],
    ['Stainless Steel', /stainless/],
    ['Champagne', /champagne/],
    ['Chrome', /chrome/],
    ['Bronze', /bronze/],
    ['Copper', /copper/],
    ['Gold', /gold|brass/],
    ['Terracotta', /terracotta/],
    ['Woodgrain', /wood|oak|walnut|timber|tassie|ash\b|maple|teak|elm\b/],
    ['Black', /black|nero\b|onyx|charcoal/],
    ['White', /white|bianco|alabaster|ivory/],
    ['Grey', /grey|gray|concrete|cement|graphite/],
    ['Beige', /beige|tan\b|sand|taupe|almond|cream|latte/],
    ['Green', /green|sage|olive|forest/],
    ['Pink', /pink|blush|rosa/],
    ['Red', /\bred\b|maroon|burgundy/],
    ['Blue', /blue|navy|azure/],
    ['Brown', /brown|mocha|coffee|espresso|chocolate/],
  ];
  const colourOf = (text) => {
    if (!text) return null;
    const t = String(text).toLowerCase();
    const hits = [];
    for (const [name, re] of COLOUR_RULES) if (re.test(t) && hits.indexOf(name) < 0) hits.push(name);
    if (!hits.length) return null;
    return hits.length > 2 ? 'Mixed' : hits[0];
  };

  // Two producers: the rail's Colour group refines the `colours` bucket
  // (f=colours~Black); the home finish strip, the megamenu Colours chips and
  // the rail's Finish group refine the raw `finishes` value
  // (f=finishes~Brushed%20Brass, 2026-09-09). A finish name is matched to a
  // chip label exactly first, then by its derived bucket.
  const finishFromUrl = () => {
    const f = new URLSearchParams(location.search).get('f');
    if (!f) return null;
    let bucket = null;
    let finish = null;
    for (const clause of f.split('|')) {
      const i = clause.indexOf('~');
      if (i < 0) continue;
      const attr = clause.slice(0, i);
      if (attr !== 'colours' && attr !== 'finishes') continue;
      const vals = clause.slice(i + 1).split('^').filter(Boolean);
      if (vals.length !== 1) return null;   // one, or nothing
      if (attr === 'finishes') finish = vals[0]; else bucket = vals[0];
    }
    if (finish) return { key: finish, finish: finish, bucket: colourOf(finish) };
    return bucket ? { key: bucket, finish: null, bucket: bucket } : null;
  };

  const applyFilteredFinish = () => {
    const want = finishFromUrl();   // { key, finish, bucket } or null
    const chipLabel = (c) => String(c.dataset.swatchLabel || '').replace(/\s*\(\d+\)$/, '').trim().toLowerCase();
    document.querySelectorAll('.product-grid .card').forEach((card) => {
      const img = card.querySelector('.card__media img:not(.card__media-alt)');
      if (!img) return;
      if (!('baseSrc' in img.dataset)) {
        img.dataset.baseSrc = img.getAttribute('src') || '';
        img.dataset.baseSrcset = img.getAttribute('srcset') || '';
      }
      let chip = null;
      if (want) {
        // SSR chips carry data-swatch-preview; island chips carry data-fin-img.
        // Match by deriving each chip label's bucket, not by string equality.
        const chips = card.querySelectorAll('.card__swatches [data-swatch-preview], .card__swatches [data-fin-img]');
        if (want.finish) {
          const exact = want.finish.toLowerCase();
          chip = Array.prototype.find.call(chips, (c) => chipLabel(c) === exact) || null;
        }
        if (!chip && want.bucket) chip = Array.prototype.find.call(chips, (c) => colourOf(c.dataset.swatchLabel) === want.bucket) || null;
      }
      if (chip) {
        const url = chip.dataset.swatchPreview || chip.dataset.finImg;
        if (!url || img.dataset.filterFinish === want.key) return;
        img.removeAttribute('srcset');
        img.src = url;
        img.dataset.filterFinish = want.key;
        // hover-out must come back HERE, not to the default photo
        img.dataset.origSrc = url;
        img.dataset.origSrcset = '';
      } else if ('filterFinish' in img.dataset) {
        delete img.dataset.filterFinish;
        img.src = img.dataset.baseSrc;
        if (img.dataset.baseSrcset) img.setAttribute('srcset', img.dataset.baseSrcset);
        else img.removeAttribute('srcset');
        delete img.dataset.origSrc;
        delete img.dataset.origSrcset;
      }
    });
  };

  applyFilteredFinish();
  document.addEventListener('plp:ready', applyFilteredFinish);
  document.addEventListener('section:loaded', applyFilteredFinish);
  // the island rewrites the URL with replaceState (no popstate) and re-renders
  // the grid, so the grid mutating IS the signal that the refinement changed
  if ('MutationObserver' in window) {
    let ft = 0;
    new MutationObserver((recs) => {
      for (const r of recs) {
        if (!r.addedNodes.length) continue;
        clearTimeout(ft);
        ft = setTimeout(applyFilteredFinish, 60);
        return;
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ---------- card image carousel (hover arrows) ----------
   * Cards emit data-card-images (JSON list of CDN renditions at width 800);
   * the arrows page the card's MAIN frame through them in place.
   *
   * Three behaviours write that one <img> and must not fight:
   *   1. alt-image crossfade  — CSS only, suppressed by .is-browsing
   *   2. swatch variant preview — .is-variant-preview, above
   *   3. paging                — .is-browsing, here
   * They share ONE stash (dataset.origSrc/origSrcset, written once by
   * whichever fires first) so any of them can hand the frame back intact.
   * Hovering a finish chip drops paging state, and paging always derives its
   * starting point from whatever is on screen — so a swatch preview simply
   * becomes the position the arrows continue from. */
  (() => {
    const conn = navigator.connection || {};
    const thrifty = conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '');
    const idx = new WeakMap();   // .card__media -> current index
    const warmed = new Set();
    const swept = new WeakSet();
    const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 250));
    // Identity of an image = its CDN path. The renditions being compared
    // differ in protocol (Liquid emits //shop/cdn/shop/files/x.jpg, the DOM
    // reports https://…) AND in width — resolving to a pathname drops
    // protocol, host and query in one step.
    const key = (u) => { try { return new URL(u, location.href).pathname; } catch { return (u || '').split('?')[0]; } };
    const imagesOf = (media) => { try { return JSON.parse(media.dataset.cardImages || '[]'); } catch { return []; } };
    const mainImg = (media) => media.querySelector('img:not(.card__media-alt)');
    const warm = (url, hi) => {
      if (thrifty || !url || warmed.has(url)) return;
      warmed.add(url);
      const i = new Image();
      i.fetchPriority = hi ? 'high' : 'low';
      i.decoding = 'async';
      i.src = url;
    };

    document.addEventListener('click', (e) => {
      const arrow = e.target.closest && e.target.closest('[data-card-prev],[data-card-next]');
      if (!arrow) return;
      // the arrows sit inside the card's own <a> — never navigate off them
      e.preventDefault();
      e.stopPropagation();
      const media = arrow.closest('.card__media');
      const card = arrow.closest('.card');
      if (!media || !card) return;
      const imgs = imagesOf(media);
      const img = mainImg(media);
      if (!img || imgs.length < 2) return;
      if (!('origSrc' in img.dataset)) {
        img.dataset.origSrc = img.getAttribute('src') || '';
        img.dataset.origSrcset = img.getAttribute('srcset') || '';
      }
      // resume paging, else pick up from whatever the frame shows (original
      // image, or the variant a swatch hover previewed)
      let at = card.classList.contains('is-browsing') && idx.has(media) ? idx.get(media) : -1;
      if (at < 0) {
        // .src, NOT .currentSrc: currentSrc only catches up with an assignment
        // on a later frame, so right after a swatch hover it still names the
        // previous image and paging would restart from the wrong position
        const cur = key(img.getAttribute('src') || '');
        at = imgs.findIndex((u) => key(u) === cur);
        if (at < 0) at = 0;
      }
      const step = arrow.hasAttribute('data-card-next') ? 1 : -1;
      const next = (at + step + imgs.length) % imgs.length;
      idx.set(media, next);
      img.removeAttribute('srcset');
      img.src = imgs[next];
      card.classList.add('is-browsing');
      card.classList.remove('is-variant-preview');
      // the NEXT press must not wait on the network
      warm(imgs[(next + 1) % imgs.length], true);
      warm(imgs[(next - 1 + imgs.length) % imgs.length], true);
    });

    // a finish chip takes the frame back: forget where paging was, so the next
    // arrow press continues from the previewed variant instead of jumping
    document.addEventListener('pointerover', (e) => {
      const chip = e.target.closest && e.target.closest('.card__swatches [data-swatch-preview]');
      const card = chip && chip.closest('.card');
      if (!card) return;
      card.classList.remove('is-browsing');
      const media = card.querySelector('.card__media');
      if (media) idx.delete(media);
    });

    // first hover on a card warms the rest of its strip in idle time
    document.addEventListener('pointerover', (e) => {
      const card = e.target.closest && e.target.closest('.card');
      if (!card || swept.has(card)) return;
      const media = card.querySelector('.card__media[data-card-images]');
      if (!media) return;
      swept.add(card);
      const imgs = imagesOf(media);
      if (imgs.length > 1) idle(() => imgs.forEach((u) => warm(u, false)));
    });

    // leaving the card hands the frame back to the original image
    document.addEventListener('pointerout', (e) => {
      const card = e.target.closest && e.target.closest('.card');
      if (!card || !card.classList.contains('is-browsing')) return;
      if (e.relatedTarget && card.contains(e.relatedTarget)) return;
      card.classList.remove('is-browsing');
      const media = card.querySelector('.card__media');
      if (media) idx.delete(media);
      const img = media && mainImg(media);
      if (!img || !('origSrc' in img.dataset)) return;
      img.src = img.dataset.origSrc;
      if (img.dataset.origSrcset) img.setAttribute('srcset', img.dataset.origSrcset);
    });
  })();

  /* ---------- delivery window dates ----------
   * snippets/delivery-estimate.liquid emits business-day OFFSETS, never dates:
   * card and PLP HTML is cached hard, so a server-rendered date is stale the
   * day after it was built. Resolve them here, on every view. */
  (() => {
    const ORD = ['th', 'st', 'nd', 'rd'];
    const ordinal = (n) => { const v = n % 100; return n + (ORD[(v - 20) % 10] || ORD[v] || ORD[0]); };
    const addBusinessDays = (n) => {
      const d = new Date();
      let left = Math.max(0, n);
      while (left > 0) {
        d.setDate(d.getDate() + 1);
        const w = d.getDay();
        if (w !== 0 && w !== 6) left--;   // Sat/Sun don't count
      }
      return d;
    };
    const fmt = (d) => {
      try {
        return d.toLocaleDateString('en-AU', { weekday: 'short' }) + ' '
          + ordinal(d.getDate()) + ' '
          + d.toLocaleDateString('en-AU', { month: 'short' });
      } catch { return ''; }
    };
    const paint = (scope) => {
      (scope || document).querySelectorAll('[data-delivery-window]').forEach((el) => {
        if (el.dataset.deliveryDone) return;
        const min = parseInt(el.dataset.minDays, 10);
        const max = parseInt(el.dataset.maxDays, 10);
        if (isNaN(min) || isNaN(max)) return;
        const from = fmt(addBusinessDays(min));
        const to = fmt(addBusinessDays(max));
        if (!from || !to) return;   // keep the Liquid fallback wording
        el.dataset.deliveryDone = '1';
        el.textContent = from === to ? 'Delivered ' + from : 'Delivered between ' + from + ' - ' + to;
      });
    };
    paint();
    // quick view, island tiles and infinite scroll all arrive after first paint
    let t = null;
    new MutationObserver(() => { clearTimeout(t); t = setTimeout(() => paint(), 120); })
      .observe(document.body, { childList: true, subtree: true });
  })();

  /* ---------- mobile nav: drill-down drawer ----------
   * Levels are Liquid-rendered and absolutely stacked; a stack of keys says
   * which one is current. Everything else follows from that: the current
   * level sits at 0, its ancestors push left (.is-prev), the rest park off
   * the right edge, the bar retitles itself, and the back button appears at
   * depth > 0. Both toggles (header + bottom bar) share one open state
   * through aria-expanded, which is also what drives the icon morph.
   *
   * The drawer sits BELOW the header so the header toggle stays on screen and
   * visibly morphs dots <-> X as the open/close control; there is no close
   * button inside the drawer, and search lives in the header bar. */
  (() => {
    const mnav = $('#MobileNav');
    if (!mnav) return;
    const panel = $('.mnav__panel', mnav);
    const titleEl = $('[data-mnav-title]', mnav);
    const backBtn = $('[data-mnav-back]', mnav);
    const header = $('.site-header');
    const levels = [...mnav.querySelectorAll('[data-mnav-level]')];
    const byKey = new Map(levels.map((l) => [l.dataset.key, l]));
    if (!panel || !titleEl || !levels.length) return;

    let stack = ['root'];
    let opener = null;

    const isOpen = () => mnav.getAttribute('aria-hidden') === 'false';
    const eachToggle = (fn) => document.querySelectorAll('[data-mnav-toggle]').forEach(fn);

    const paint = () => {
      const current = stack[stack.length - 1];
      const ancestors = new Set(stack.slice(0, -1));
      levels.forEach((l) => {
        const k = l.dataset.key;
        const on = k === current;
        l.classList.toggle('is-current', on);
        l.classList.toggle('is-prev', ancestors.has(k));
        l.setAttribute('aria-hidden', on ? 'false' : 'true');
        // inert is the modern half; visibility:hidden in the CSS covers the rest
        if (on) l.removeAttribute('inert'); else l.setAttribute('inert', '');
      });
      const lvl = byKey.get(current);
      titleEl.textContent = (lvl && lvl.dataset.title) || 'Menu';
      backBtn.hidden = stack.length < 2;
      if (!backBtn.hidden) {
        const parent = byKey.get(stack[stack.length - 2]);
        backBtn.setAttribute('aria-label', 'Back to ' + ((parent && parent.dataset.title) || 'Menu'));
      }
      if (lvl) lvl.scrollTop = 0;
    };

    const go = (next) => { stack = next; paint(); titleEl.focus(); };

    // Park the drawer between the header's real bottom edge and the top of the
    // bottom bar. Measured rather than hard-coded: an announcement bar would
    // otherwise hide the toggle behind the scrim, and the bottom bar (lifted
    // above the scrim so it stays usable) would cover the drawer's own foot.
    const bar = $('.bottom-bar');
    const setBounds = () => {
      const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 56;
      const shown = bar && getComputedStyle(bar).display !== 'none';
      mnav.style.setProperty('--mnav-top', top + 'px');
      mnav.style.setProperty('--mnav-bottom', (shown ? Math.round(bar.getBoundingClientRect().height) : 0) + 'px');
    };

    const open = () => {
      setBounds();
      mnav.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      eachToggle((t) => t.setAttribute('aria-expanded', 'true'));
      stack = ['root'];
      paint();
      // the drawer itself slides for 300ms — focus after it has arrived
      setTimeout(() => titleEl.focus(), 320);
    };
    const close = () => {
      mnav.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      eachToggle((t) => t.setAttribute('aria-expanded', 'false'));
      // aria-expanded above is cleared on EVERY close route — the toggle
      // itself, the scrim / [data-mnav-close], Escape, a rotate to desktop —
      // because they all funnel through this one function. What was NOT
      // handled was the focus the toggle keeps after a tap: see releaseFocus.
      releaseFocus(opener);
      // the toggle the finger actually landed on is not always `opener` (open
      // with the header's, close with the bottom bar's), so clear any ring the
      // other instances are wearing too
      if (!byKeyboard()) eachToggle((t) => { if (document.activeElement === t) t.blur(); });
      opener = null;
    };

    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-mnav-toggle]');
      if (toggle) {
        e.preventDefault();
        if (isOpen()) { close(); } else { opener = toggle; open(); }
        return;
      }
      /* Search is the header's universal bar at every width now. The bottom
       * bar's Search button forwards to that same toggle, and either entry
       * point resets the drawer first so the two overlays never stack.
       *
       * Luke, 2026-09-20 14:30 Sydney, iPhone at 390: "In the PWA style menu
       * when I click the search it doesn't allow the user to actually search
       * as there's nowhere to type. As we already have the search bar visible
       * below the navbar perhaps we should just pass the cursor to that search
       * field so the user can search using the prescribed way."
       *
       * ROOT CAUSE, measured on the preview at 390x844, PDP, scrollY 1654:
       *   html.header-hidden           true
       *   header group computed top    -141px   (box -141..-2)
       *   [data-usearch-input]         314x42 at y -56..-14   <- off screen
       *   [data-search-dialog]         OPEN, -3..841, "What are you looking for?"
       *   document.activeElement       input.usearch__input   <- FOCUSED
       * The field was never missing and the focus never failed. In reveal mode
       * a downward scroll puts .header-hidden on <html>, and theme.css lifts
       * the whole sticky header group by --header-h + 2px. The header's static
       * search field goes up with it; .sdialog, which is absolutely positioned
       * at top:100% of the bar, is ~840px tall and stays on screen. So the
       * shopper gets the panel, the chips and the tiles with the one element
       * that takes typing sitting 56px above the top of the screen — "nowhere
       * to type", exactly.
       *
       * THE FIX IS THE CLASS, NOT A SCROLL. Removing .header-hidden is enough:
       * the group is position:sticky at top:0, so the field comes back to the
       * top of the viewport without the page moving at all, and the shopper
       * keeps their place on the product they were reading. A scrollTo(0) —
       * the obvious fix — would have thrown that away on every search. The
       * page is only scrolled when the header is NOT sticky (a template or a
       * header_scroll_mode where it sits in flow), because then the field
       * really is at the top of the document and nothing else brings it back.
       *
       * FOCUS IS SYNCHRONOUS, inside the tap's user activation: iOS Safari
       * raises the keyboard for a focus() called during the gesture and
       * declines one called from a timer, and usearch.js's own open() focuses
       * from setTimeout(...,0). preventScroll, because the header's `top` is
       * mid-transition (.28s) at this instant and letting the browser scroll
       * to an element that is still moving is how #2026-09-08 put the page at
       * the top by accident.
       *
       * THEN THE RE-PLACE. search-dialog.js caps the panel's height off the
       * bar's bottom edge (--sd-top) and only recomputes on resize, so the
       * bar moving from -2 to +139 would otherwise leave the panel 141px too
       * tall for the rest of this interaction. One resize now and one after
       * the transition — 320ms is the settle the header's own condensed-search
       * handler already uses for this transition. */
      const search = e.target.closest('[data-search-toggle]');
      if (search && !mnav.contains(search)) {
        e.preventDefault();
        if (isOpen()) close();
        const root = document.documentElement;
        root.classList.remove('header-hidden');
        const ut = header && header.querySelector('[data-usearch-toggle]');
        // usearch.js's own resolver: the Typesense island swaps a live input
        // into the slot, and that one is the real field once it exists.
        const field = () => header && (header.querySelector('[data-usearch-slot]:not([hidden]) input')
          || header.querySelector('[data-usearch-input]'));
        // the toggle runs first: on the legacy header it is what UNHIDES the
        // bar, so the field has no box until it has run.
        if (ut) ut.click();
        const input = field();
        if (!input) {
          // no field on this template at all — /search renders one and focuses
          // it on load, which is the same destination by a slower road.
          window.location.href = (window.routes || {}).search_url || '/search';
          return;
        }
        const grp = header.closest('.shopify-section-group-header-group') || header;
        if (getComputedStyle(grp).position !== 'sticky') window.scrollTo({ top: 0 });
        input.focus({ preventScroll: true });
        window.dispatchEvent(new Event('resize'));
        setTimeout(() => window.dispatchEvent(new Event('resize')), 320);
        return;
      }
      if (e.target.closest('[data-usearch-toggle]')) { if (isOpen()) close(); return; }
      if (e.target.closest('[data-mnav-close]')) { e.preventDefault(); close(); return; }
      const drill = e.target.closest('[data-mnav-drill]');
      if (drill && mnav.contains(drill)) {
        e.preventDefault();
        const key = drill.getAttribute('data-mnav-drill');
        if (byKey.has(key)) go(stack.concat(key));
        return;
      }
      if (e.target.closest('[data-mnav-back]')) {
        e.preventDefault();
        if (stack.length > 1) go(stack.slice(0, -1));
      }
    });

    // No focus trap: the drawer is a disclosure panel under a live header, not
    // a modal. The toggle that closes it, search and the bottom bar all sit
    // OUTSIDE the panel by design, so trapping would strand the one control
    // that dismisses it. Tab therefore runs header -> drawer -> bottom bar,
    // which is also the reading order; Escape and the scrim still close.
    document.addEventListener('keydown', (e) => {
      if (isOpen() && e.key === 'Escape') { e.preventDefault(); close(); }
    });

    // a rotate to desktop while the drawer is open would otherwise leave the
    // body scroll-locked behind a hidden panel; a rotate that keeps it open
    // needs the header re-measured
    const wide = window.matchMedia('(min-width:901px)');
    const onWide = (ev) => { if (ev.matches && isOpen()) close(); };
    if (wide.addEventListener) wide.addEventListener('change', onWide);
    window.addEventListener('resize', () => { if (isOpen()) setBounds(); }, { passive: true });
    paint();
  })();

  /* ---------- Chat, as the bottom nav's sixth item ----------
   * Luke, 2026-09-20 14:26 Sydney, iPhone screenshot at 390 of a PDP scrolled
   * to "The detail", Inbox's floating pill sitting over the bar's Account
   * item: "In mobile view the floating chat is showing over the top of the PWA
   * style menu, it should show as another menu item in the PWA menu when that
   * menu is active."
   *
   * So the bar carries the affordance and theme.css hides Inbox's launcher in
   * the whole <=900px band — but ONLY under body.bbar-chat, which is set here
   * and only once a real <shopify-chat> has been found and the item unhidden.
   * The class is the contract: no class, no hide, so a phone can never be left
   * with no way to reach chat.
   *
   * PRESENCE-GATED, the same contract as assets/sticky-buy-bar.js and
   * src/islands/cart/ChatDock.jsx: the Inbox app embed appends exactly one
   * <shopify-chat> to <body> (cdn.shopify.com/storefront/web-components/
   * chat.js, async), so the item ships `hidden` in the snippet and is revealed
   * on arrival. A store with the embed off shows five items, never a sixth
   * that does nothing.
   *
   * OPEN PATH: show()/close(), shopify-chat's own public methods, with the
   * `open` attribute as the observed fallback — character for character what
   * ChatDock.jsx and sticky-buy-bar.js already do. Nothing is moved in the DOM
   * and no Inbox setting is touched.
   *
   * THE NAV DRAWER IS CLOSED FIRST, the way the bar's Search item already
   * forwards through a close: the drawer is lifted above its own scrim while
   * open (theme.css .drawer-open .bottom-bar{z-index:94}) so this item is
   * genuinely tappable over it, and opening chat behind a full-height drawer
   * would put the panel somewhere the shopper cannot see. Routed through the
   * scrim's [data-mnav-close] rather than a second copy of close(): that is
   * the single funnel #410 established, so aria-expanded on both toggles and
   * the touch-focus release both still happen. */
  (() => {
    const btn = $('[data-bbar-chat]');
    if (!btn) return;
    let chatEl = null;
    let stateMo = null;
    let watch = null;
    let timer = null;

    /* aria only. The VISIBLE label stays "Chat" in both states: this is a
     * six-cell flex row and a label that grows to "Close chat" would reflow
     * every other cell the moment the panel opened. */
    const paint = () => {
      if (!chatEl) return;
      const open = chatEl.hasAttribute('open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      btn.setAttribute('aria-label', open ? (btn.dataset.labelClose || 'Close chat') : (btn.dataset.labelOpen || 'Chat'));
    };

    const wire = (el) => {
      if (chatEl) return;
      chatEl = el;
      btn.hidden = false;
      document.body.classList.add('bbar-chat');
      btn.addEventListener('click', () => {
        if (!chatEl) return;
        if (chatEl.hasAttribute('open')) {
          if (typeof chatEl.close === 'function') chatEl.close();
          else chatEl.removeAttribute('open');
        } else {
          if (document.body.classList.contains('drawer-open')) {
            const scrim = $('[data-mnav-close]');
            if (scrim) scrim.click();
          }
          if (typeof chatEl.show === 'function') chatEl.show();
          else chatEl.setAttribute('open', '');
        }
        paint();
      });
      stateMo = new MutationObserver(paint);
      stateMo.observe(chatEl, { attributes: true, attributeFilter: ['open'] });
      paint();
    };

    const already = document.querySelector('shopify-chat');
    if (already) { wire(already); return; }
    // chat.js appends straight to <body>, so a childList watch on body is
    // enough — no subtree. Given up on after 30s: the embed is off, or its
    // script never loaded, and either way the item stays hidden.
    watch = new MutationObserver(() => {
      const el = document.querySelector('shopify-chat');
      if (!el) return;
      watch.disconnect(); watch = null;
      clearTimeout(timer);
      wire(el);
    });
    watch.observe(document.body, { childList: true });
    timer = setTimeout(() => { if (watch) { watch.disconnect(); watch = null; } }, 30000);
    addEventListener('pagehide', () => { watch?.disconnect(); stateMo?.disconnect(); clearTimeout(timer); });
  })();

  /* ---------- recently viewed (localStorage rail) ----------
   *
   * Luke, 2026-09-21, a PDP screenshot beside a /collections/showers tile:
   * "recently viewed and other components like this should be carousels and
   * also should have the same features like quick view, left/right for cycling
   * through that product's images, add to project and so on."
   *
   * So this no longer draws a tile of its own. It stores the product AS A CARD
   * DOCUMENT — snippets/card-json.liquid, the same shape a Typesense hit has —
   * and hands it to window.STCard.render(), the one renderer the PLP grid and
   * the search dialog already draw through. Quick view, the image pager, add
   * to project, add to cart, badges, brand + type, price + RRP and the swatch
   * strip then arrive from the delegates in this file, theme.js and
   * project-list.js, with nothing wired here at all.
   *
   * STORAGE SHAPE. Key `st_recent`, unchanged, most recent first, capped at 12.
   * An entry is now the card document with `v: 2` on it. Entries written before
   * 2026-09-21 have no `v` and carry only {handle,title,image,price,url}.
   *
   * Those are KEPT, not discarded — the history is the shopper's — but they are
   * not drawn, and each is promoted to v2 the next time that product is opened.
   * The tempting alternative, upgrading them from /products/<handle>.js, was
   * rejected: that endpoint serves no metafields, so it cannot tell a
   * call-for-price or in-store-only product from an ordinary one, and a card
   * drawn from it would offer Add to cart on a product that must never have
   * one. A rail that is short for a session beats a rail that sells something
   * the store does not sell online.
   *
   * An image is only ever a CDN URL. A product with no featured image used to
   * render `Liquid error (sections/main-product line 572): invalid url input`
   * into data-image, which was stored and then requested as an <img src> on
   * every later PDP (seen on production 2026-09-17). The template no longer
   * emits it; entries already holding one are still scrubbed. */
  (() => {
    const RKEY = 'st_recent';
    const MAX = 12;
    const okImg = (u) => typeof u === 'string' && /^(https?:)?\/\//.test(u);
    const read = () => {
      try { const l = JSON.parse(localStorage.getItem(RKEY) || '[]'); return Array.isArray(l) ? l : []; }
      catch { return []; }
    };
    const write = (l) => { try { localStorage.setItem(RKEY, JSON.stringify(l)); } catch { /* blocked or full */ } };
    const handleOf = (e) => (e && typeof e === 'object' && typeof e.handle === 'string' ? e.handle : '');

    const cur = document.querySelector('[data-rv-product]');
    let list = read();
    if (cur) {
      let doc = null;
      try { doc = JSON.parse(cur.textContent || 'null'); } catch { doc = null; }
      if (doc && doc.handle) {
        doc.v = 2;
        if (!okImg(doc.image)) doc.image = '';
        // most recent first, this product moved to the front, never twice
        list = [doc, ...list.filter((x) => handleOf(x) !== doc.handle)].slice(0, MAX);
        write(list);
      }
    }

    const sec = document.querySelector('[data-recently-viewed]');
    const track = sec && sec.querySelector('[data-rail-track]');
    if (!sec || !track) return;

    const skip = cur ? cur.dataset.handle : null;
    const seen = new Set();
    const items = [];
    for (const e of list) {
      const h = handleOf(e);
      // the product being looked at is never in its own recently-viewed rail
      if (!h || h === skip || seen.has(h)) continue;
      seen.add(h);
      if (e.v === 2) items.push(e);
      if (items.length >= MAX) break;
    }

    // nothing drawable — the block hides itself again, including the height
    // the parse-time script in snippets/recently-viewed.liquid reserved
    if (!items.length || !window.STCard) { track.innerHTML = ''; sec.hidden = true; return; }

    track.innerHTML = items
      .map((doc, i) => `<li class="prail__cell">${window.STCard.render(doc, i + 1)}</li>`)
      .join('');
    sec.hidden = false;
    // product-rail.js observes the subtree and will wire/measure this track;
    // calling sync directly is the belt to that braces when it is already up
    if (window.STRail) window.STRail.sync(sec);
  })();

  /* ---------- postcode -> delivery zone (client-side heuristic; wire a real
   * freight API later). The PDP's Delivery card (purchase-block.js) owns the
   * form and composes this with the category lead time; exposed so it stays
   * one table. */
  const etaFor = (pc) => {
    const n = parseInt(pc, 10);
    if (isNaN(n) || !/^\d{4}$/.test(String(pc).trim())) return null;
    const metro = [[2000, 2234], [2555, 2574], [3000, 3207], [4000, 4179], [5000, 5199], [6000, 6199], [2600, 2620]];
    if (metro.some(([a, b]) => n >= a && n <= b)) return 'Metro delivery — 1–3 business days';
    if (n >= 800 && n <= 899) return 'NT delivery — 5–10 business days';
    if (n >= 6700) return 'Regional WA — 5–10 business days';
    return 'Regional delivery — 3–7 business days';
  };
  window.SydneyTaps = window.SydneyTaps || {};
  window.SydneyTaps.postcodeEta = etaFor;

  /* ---------- back-in-stock ---------- */
  document.addEventListener('variant:change', (e) => {
    const bis = (e.target.closest('.product, .qv') || document).querySelector('[data-bis]');
    if (bis && e.detail) { bis.hidden = e.detail.available; bis.dataset.variantId = e.detail.id; }
  });
  /* THE DEFECT THIS REPLACES (2026-09-22). The old handler read
     `if (f.dataset.endpoint) await fetch(...)` and then, UNCONDITIONALLY, wrote
     "Thanks — we'll email you the moment it's back in stock." With
     settings.bis_endpoint blank on this store — and settings.enable_bis
     defaulting to TRUE, so the form rendered on every sold-out variant — that
     sentence was printed over a request that was never made. Every restock
     request ever made here was thrown away with a thank-you.
     Now: the form does not render at all without a target
     (snippets/back-in-stock.liquid decides), the reply is READ, a failure says
     so, and the acknowledgement is the app's own wording — which deliberately
     does not promise a watch, because the sender that would do the watching is
     not built yet. */
  document.addEventListener('submit', async (e) => {
    const f = e.target.closest('[data-bis]');
    if (!f) return;
    e.preventDefault();
    const emailEl = f.querySelector('[data-bis-email]');
    const email = (emailEl && emailEl.value || '').trim();
    const msg = f.querySelector('[data-bis-msg]');
    const btn = f.querySelector('button[type="submit"]');
    const optIn = f.querySelector('[data-bis-optin]');
    const hp = f.querySelector('.bis__hp');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      msg.textContent = 'Please enter an email address we can reach you on.';
      msg.hidden = false;
      return;
    }
    if (btn) btn.disabled = true;
    try {
      const res = await fetch(f.dataset.endpoint, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          email,
          variant_id: f.dataset.variantId,
          product_id: f.dataset.productId || '',
          // Spam Act: a separate, unticked box, sent as its own field so the
          // app records the two consents apart.
          marketing_opt_in: !!(optIn && optIn.checked),
          website: hp ? hp.value : '',
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || data.ok !== true) throw new Error((data && data.message) || 'not saved');
      document.dispatchEvent(new CustomEvent('bis:subscribe', { detail: { email, variant_id: f.dataset.variantId } }));
      msg.textContent = data.message || 'Thanks — we have your request.';
    } catch {
      msg.textContent = "We couldn't save that — please try again.";
    }
    if (btn) btn.disabled = false;
    msg.hidden = false;
  });

  /* ---------- generic tabs (shop-by-brand, etc.) ---------- */
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-tab]');
    if (!tab) return;
    const root = tab.closest('[data-tabs]'); if (!root) return;
    root.querySelectorAll('[data-tab]').forEach((t) => { const on = t === tab; t.classList.toggle('is-active', on); t.setAttribute('aria-selected', on ? 'true' : 'false'); });
    root.querySelectorAll('[data-tab-panel]').forEach((p) => { p.hidden = p.id !== tab.dataset.target; });
  });

  /* ---------- PWA install prompt: REMOVED 2026-09-12 ----------
     The beforeinstallprompt capture, the [data-pwa-install] / [data-pwa-dismiss]
     click branches, the #PWAInstall lookup, the deferredPrompt state and the
     localStorage 'pwa_dismissed' flag (read nowhere else) all went with the
     pill in snippets/pwa.liquid — it covered the sticky buy bar and its x
     could not dismiss it. The manifest and the meta tags stay, so the site is
     still installable from the browser's own menu; nothing here listens for
     the install event any more. docs/decisions/2026-09-12-pwa-install-prompt.md. */

  /* ---------- lazy product recommendations (Shopify native, Section Rendering API) ---------- */
  document.querySelectorAll('[data-recommendations]').forEach((el) => {
    const load = async () => { try { const r = await fetch(el.dataset.url); el.innerHTML = await r.text(); } catch {} };
    if (!('IntersectionObserver' in window)) return load();
    const io = new IntersectionObserver((ents) => { if (ents.some((x) => x.isIntersecting)) { load(); io.disconnect(); } }, { rootMargin: '400px' });
    io.observe(el);
  });

  /* ---------- newsletter modal (delay + exit-intent, once) ---------- */
  const nl = $('#NewsletterModal');
  if (nl && !localStorage.getItem('nl_dismissed')) {
    const show = () => { if (sessionStorage.getItem('nl_seen')) return; sessionStorage.setItem('nl_seen', '1'); nl.setAttribute('aria-hidden', 'false'); document.body.classList.add('drawer-open'); };
    const timer = setTimeout(show, (parseInt(nl.dataset.delay, 10) || 12) * 1000);
    document.addEventListener('mouseout', (e) => { if (e.clientY <= 0 && !e.relatedTarget) { clearTimeout(timer); show(); } });
    document.addEventListener('click', (e) => { if (e.target.closest('[data-nl-close]')) { nl.setAttribute('aria-hidden', 'true'); document.body.classList.remove('drawer-open'); localStorage.setItem('nl_dismissed', '1'); } });
  }

  /* ---------- countdown timers ---------- */
  document.querySelectorAll('[data-countdown]').forEach((el) => {
    const dl = new Date((el.dataset.deadline || '').replace(' ', 'T')).getTime();
    if (isNaN(dl)) return;
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      let d = Math.max(0, dl - Date.now());
      const D = Math.floor(d / 864e5); d %= 864e5; const H = Math.floor(d / 36e5); d %= 36e5; const M = Math.floor(d / 6e4); const S = Math.floor((d % 6e4) / 1e3);
      el.querySelector('[data-cd-d]').textContent = pad(D); el.querySelector('[data-cd-h]').textContent = pad(H);
      el.querySelector('[data-cd-m]').textContent = pad(M); el.querySelector('[data-cd-s]').textContent = pad(S);
    };
    tick(); setInterval(tick, 1000);
  });

  /* review submission: REMOVED 2026-09-10 with the native review form it
     served (sections/product-reviews.liquid). It said "Thanks - your review
     has been submitted for moderation" whether or not anything was sent:
     the POST was skipped entirely when settings.review_endpoint was blank,
     which it always has been. Reviews come from a review app now; the app
     owns its own submit path. */

  /* ---------- generic swatch hover popover (A-2 pattern) ----------
     Any element carrying data-swatch-label grows an ink tooltip above it
     (label, plus an image when data-swatch-img is set). Shared by the
     filter-rail colour swatches, PDP finish swatches and card strips. */
  (() => {
    let pop = null, anchor = null;
    const hide = () => { if (pop) pop.classList.remove('is-on'); anchor = null; };
    document.addEventListener('pointerover', (e) => {
      // A tooltip is hover UI, and a finger cannot hover. On iOS a tap
      // dispatches pointerover (pointerType 'touch') BEFORE the click, and
      // WebKit's content-change observer watches what that hover phase makes
      // visible: if hover handlers show content, the tap is treated as a
      // hover and the click is withheld (Luke, 2026-09-12, iPhone: "every
      // other tap fails to switch variant"). Not reproduced on the iOS
      // 18.6 / 26.3 simulators, but this is the one hover-only surface a
      // touch tap on a swatch used to open, so touch never opens it: the
      // legend beside the chips already names the finish. Mouse and pen
      // keep the tooltip. scripts/swatch-tap-gate.mjs asserts this.
      if (e.pointerType === 'touch') { if (anchor) hide(); return; }
      const sw = e.target.closest ? e.target.closest('[data-swatch-label]') : null;
      if (!sw) { if (anchor) hide(); return; }
      if (sw === anchor) return;
      anchor = sw;
      if (!pop) {
        pop = document.createElement('div');
        pop.className = 'swpop';
        pop.setAttribute('role', 'tooltip');
        pop.innerHTML = '<img alt="" hidden><i class="swpop__sw" aria-hidden="true"></i><span></span>';
        document.body.appendChild(pop);
      }
      const img = pop.querySelector('img'), lab = pop.querySelector('span');
      if (sw.dataset.swatchImg) { img.src = sw.dataset.swatchImg; img.hidden = false; }
      else { img.hidden = true; img.removeAttribute('src'); }
      // colour-only swatches preview their --sw (solid or gradient) in the
      // panel; without this the popover showed a blank white box
      const swVal = getComputedStyle(sw).getPropertyValue('--sw').trim();
      pop.classList.toggle('swpop--colour', !sw.dataset.swatchImg && !!swVal);
      pop.style.setProperty('--sw', swVal || '#cfccc6');
      lab.textContent = sw.dataset.swatchLabel;
      pop.classList.add('is-on');
      const r = sw.getBoundingClientRect();
      const pw = pop.offsetWidth, ph = pop.offsetHeight;
      let x = Math.max(8, Math.min(r.left + r.width / 2 - pw / 2, innerWidth - pw - 8));
      let y = r.top - ph - 10;
      const below = y < 8;
      pop.classList.toggle('swpop--below', below);
      if (below) y = r.bottom + 10;
      pop.style.left = Math.round(x) + 'px';
      pop.style.top = Math.round(y) + 'px';
    });
    document.addEventListener('pointerout', (e) => {
      if (!anchor) return;
      const to = e.relatedTarget;
      if (!to || !(to.closest && to.closest('[data-swatch-label]') === anchor)) {
        if (e.target.closest && e.target.closest('[data-swatch-label]') === anchor) hide();
      }
    });
    addEventListener('scroll', hide, { passive: true, capture: true });
  })();

  /* ---------- aggressive swatch-preview prefetch ----------
   * Hover-swapping a card to a variant image (data-swatch-preview) must be
   * instant, so the images are warmed long before the hover: near-viewport
   * cards as they approach (600px lookahead), the rest of the grid in idle
   * batches after load, and a high-priority boost the moment a card is
   * entered. CDN images cache normally (the password gate only no-stores
   * HTML), and HTTP/2 multiplexes the batches on one connection. */
  (() => {
    const conn = navigator.connection || {};
    if (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '')) return;
    const slowNet = (conn.effectiveType || '') === '3g'; // viewport+hover tiers only
    const warmed = new Set();
    const warm = (url, hi) => {
      if (!url || warmed.has(url)) return;
      warmed.add(url);
      const i = new Image();
      i.fetchPriority = hi ? 'high' : 'low';
      i.decoding = 'async';
      i.src = url;
    };
    const warmScope = (scope, hi) =>
      scope.querySelectorAll('[data-swatch-preview]').forEach((c) => warm(c.dataset.swatchPreview, hi));

    // tier 1: cards approaching the viewport
    let io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((ents) => ents.forEach((en) => {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        warmScope(en.target, false);
      }), { rootMargin: '600px' });
    }
    const seen = new WeakSet();
    const scan = () => {
      document.querySelectorAll('.card__swatches').forEach((strip) => {
        if (seen.has(strip)) return;
        seen.add(strip);
        if (io) io.observe(strip); else warmScope(strip, false);
      });
    };
    scan();

    // tier 2: idle sweep over the whole grid, 8 per tick so we never contend
    const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 250));
    const sweep = () => {
      const rest = [...document.querySelectorAll('[data-swatch-preview]')]
        .filter((c) => !warmed.has(c.dataset.swatchPreview));
      rest.slice(0, 8).forEach((c) => warm(c.dataset.swatchPreview, false));
      if (rest.length > 8) idle(sweep);
    };
    if (!slowNet) {
      if (document.readyState === 'complete') idle(sweep);
      else addEventListener('load', () => idle(sweep), { once: true });
    }

    // late grids (island re-render, infinite scroll): rescan, debounced
    let mt = null;
    new MutationObserver(() => {
      clearTimeout(mt);
      mt = setTimeout(() => { scan(); if (!slowNet) idle(sweep); }, 200);
    }).observe(document.body, { childList: true, subtree: true });

    // tier 3: entering a card boosts its own previews to high priority
    document.addEventListener('pointerover', (e) => {
      const card = e.target.closest && e.target.closest('.card');
      if (card) warmScope(card, true);
    });
  })();

  /* ---------- hover-intent PDP hero prefetch ---------- */
  // Card hover = navigation intent. Speculation Rules prefetch the PDP HTML on
  // hover, but the hero image still popped in after the transition. Warm the
  // EXACT rendition the PDP's responsive-image will pick (same widths list,
  // same 52vw/100vw sizes math) — any other width is a cache miss, not a win.
  (() => {
    const conn = navigator.connection || {};
    if (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '')) return;
    const WIDTHS = [320, 480, 640, 768, 960, 1200, 1600, 2000]; // responsive-image.liquid
    const warmed = new Set();
    const MAX = 40;
    const heroURL = (img) => {
      const src = img.currentSrc || img.src;
      if (!src || src.indexOf('/cdn.shop') === -1 && src.indexOf('cdn.shopify.com') === -1) return null;
      const slot = (innerWidth >= 1000 ? 0.52 * innerWidth : innerWidth) * Math.min(devicePixelRatio || 1, 2);
      const w = WIDTHS.find((x) => x >= slot) || WIDTHS[WIDTHS.length - 1];
      try {
        const u = new URL(src, location.href);
        u.searchParams.set('width', String(w));
        return u.href;
      } catch { return null; }
    };
    let timer = null;
    const warm = (link) => {
      // the card's link is split in two (media above the brand line, the rest
      // of the body below) — hover on either half must still find the tile
      const card = link.closest('.card, .ts-hit') || link;
      const qvBtn = card.querySelector('[data-quick-view]');
      if (qvBtn) qvWarm(qvBtn.dataset.handle);
      const img = card.querySelector('.card__media img, .ts-hit__media img');
      if (!img) return;
      const url = heroURL(img);
      if (!url || warmed.has(url) || warmed.size >= MAX) return;
      warmed.add(url);
      const pre = new Image();
      pre.fetchPriority = 'low';
      pre.decoding = 'async';
      pre.src = url;
    };
    document.addEventListener('pointerover', (e) => {
      const card = e.target.closest && e.target.closest('.card__link, .ts-hit');
      if (!card) return;
      clearTimeout(timer);
      timer = setTimeout(() => warm(card), 65); // ignore drive-by passes
    });
    document.addEventListener('pointerout', () => clearTimeout(timer));
    document.addEventListener('focusin', (e) => {
      const card = e.target.closest && e.target.closest('.card__link, .ts-hit');
      if (card) warm(card); // keyboard focus = explicit intent, no debounce
    });
  })();

  /* ---------- hover-intent collection SEARCH warm ---------- */
  // Speculation Rules prefetch the destination HTML on hover; this warms the
  // other half of a PLP — its Typesense answer. window.__TSX (installed at
  // parse by snippets/search-provider.liquid, PLP templates only) fetches the
  // destination's default search and parks it in sessionStorage, so the landing
  // page's own parse-time search is a cache hit and the rail hydrates on the
  // first frame instead of after the boot chain.
  (() => {
    const conn = navigator.connection || {};
    if (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '')) return;
    const MAX = 6;                       // one hover pass over a megamenu, not the whole nav
    let warmed = 0;
    let timer = null;
    const handleOf = (a) => {
      if (!a || a.origin !== location.origin) return null;
      const m = /^\/collections\/([^/?#]+)\/?$/.exec(a.pathname);
      // /collections/all is Shopify's synthetic catalogue — the island treats
      // it as the unscoped grid, and so does the warm
      if (!m || m[1] === 'all') return null;
      if (a.search && /[?&](f|pr)=/.test(a.search)) return null; // refined URLs need the multi-query shape
      return decodeURIComponent(m[1]);
    };
    const warm = (a) => {
      const X = window.__TSX;
      if (!X || warmed >= MAX) return;
      const h = handleOf(a);
      if (h && X.warm(h)) warmed++;
    };
    document.addEventListener('pointerover', (e) => {
      const a = e.target.closest && e.target.closest('a[href*="/collections/"]');
      if (!a) return;
      clearTimeout(timer);
      timer = setTimeout(() => warm(a), 80); // same intent window as the other warmers
    });
    document.addEventListener('pointerout', () => clearTimeout(timer));
    document.addEventListener('focusin', (e) => {
      const a = e.target.closest && e.target.closest('a[href*="/collections/"]');
      if (a) warm(a); // keyboard focus = explicit intent, no debounce
    });
  })();

  // legacy STProject export removed — projects live in project-list.js (shopify-project core)
})();


/* ---- filter-rail scroll pins (A-2 model): rail scrolls with the page,
   pins top when it fits the viewport, pins bottom when taller. The CSS
   consumes --rail-top; we only measure. ResizeObserver catches facet
   hydration and Show-more expansion. ---- */
(function () {
  var rail = document.querySelector('.plp__rail');
  if (!rail) return;
  var set = function () {
    if (window.innerWidth <= 900) { rail.style.removeProperty('--rail-top'); return; }
    var header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 72;
    var topFit = header + 14;
    var h = rail.offsetHeight;
    var top = (h + topFit + 20 > window.innerHeight) ? (window.innerHeight - h - 20) : topFit;
    rail.style.setProperty('--rail-top', top + 'px');
  };
  if ('ResizeObserver' in window) new ResizeObserver(set).observe(rail);
  addEventListener('resize', set);
  set();
})();
